// The rail-v3 row actions as Antonin's catalogue takes them (left-rail target §5): each IpsAction the rail binds becomes a
// `CardAction` of @scripsteam/signal (label, icon, red when destructive, on the hover bar or card-only, its handler). A plain module
// (no component, no look): the catalogue's `barActs` picks the hover bar from these and its hover card / MedicationCard draws them.
import {
  CheckCheck,
  CircleCheck,
  CircleX,
  FileInput,
  ListChecks,
  MessageSquarePlus,
  Pencil,
  Play,
  Repeat2,
  Square,
  Stethoscope,
  Trash2,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import type { IpsAction } from '@scripsteam/scrips-signal-ds';
import type { CardAction } from '@scripsteam/signal';
import { railV3ActionWord, railV3SortActions } from './rail-v3-model';
import { isRailV3Action } from './rail-v3-actions';

/** The icon of each action, ALWAYS the glyph Antonin's catalogue gives the same action (left-rail target §4b: "Icons and look stay
 *  exactly as Antonin's catalogue"; §7.9: the Claude Design round 5 mapping, which swapped four self-picked icons back to the
 *  catalogue's: Stethoscope = Add to (encounter) diagnoses, CircleX = Entered in error, ListChecks = Reconcile, MessageSquarePlus =
 *  Add a note). The rest of the round 5 set: FileInput Add to note, CircleCheck Resolve, Pencil Edit / Edit dose, Undo2 Reactivate,
 *  Trash2 Remove, Square Stop, CheckCheck Mark allergies reviewed (PatientContextRail; mapped only: no server write for it exists, so
 *  no row offers it, §3). Refill keeps the catalogue RailForms glyph (Repeat2) and the legacy Continue its Play. "Add a note" (the
 *  pre-visit "+ Note") and the encounter's "Problem note" are the same comment on the problem record, so both take MessageSquarePlus.
 *  "Undo resolve" has no catalogue glyph: it gets none (card only) until Antonin gives it one (D-79); none is invented here. */
export const RAIL_V3_ACTION_ICON: Readonly<Record<string, LucideIcon>> = {
  'Add to note': FileInput,
  '+ Note': MessageSquarePlus,
  // visitNoteV2 (PROD-3414): the encounter's problem row keeps the problem's own note as "Problem note".
  'Problem note': MessageSquarePlus,
  Resolve: CircleCheck,
  Unresolve: Undo2,
  Edit: Pencil,
  Remove: Trash2,
  // `medActions`: a medication to the A&P Plan (left-rail target §3). Continue, Change and Stop are kept for the legacy action set.
  Refill: Repeat2,
  'Edit dose': Pencil,
  Change: Pencil,
  Continue: Play,
  Stop: Square,
  'Add to encounter diagnoses': Stethoscope,
  'Entered in error': CircleX,
  'Reconcile medications': ListChecks,
  'Mark allergies reviewed': CheckCheck,
};

/** A label the map does not know draws no glyph (never an invented one) and stays in the card only. */
const NO_ICON = (() => null) as unknown as LucideIcon;

export interface RailV3CardActionHooks {
  /** Runs the action as the rail does (its anchored surface, its note, or its own handler). */
  run: (action: IpsAction) => void;
  /** The action cannot run now: say why. The catalogue's CardAction has no disabled state (a gap), so the reason is said on select. */
  blocked: (reason: string) => void;
}

/** The row's actions as catalogue CardActions, in the rail's ruled order. Destructive ones are red; the catalogue puts them last. */
export function railV3CardActions(actions: readonly IpsAction[] | undefined, hooks: RailV3CardActionHooks): CardAction[] {
  return railV3SortActions(actions ?? []).map((a) => {
    const glyph = RAIL_V3_ACTION_ICON[a.label];
    const danger = a.tone === 'danger';
    const reason = isRailV3Action(a) ? a.disabledReason : undefined;
    // A planned action (rail-v3-actions.ts) says itself whether the bar carries it; a legacy one is on the bar unless destructive.
    const bar = !!glyph && (isRailV3Action(a) ? a.bar : !danger);
    return {
      label: railV3ActionWord(a.label),
      Icon: glyph ?? NO_ICON,
      ...(danger ? { danger: true } : {}),
      bar,
      onSelect: reason ? () => hooks.blocked(reason) : () => hooks.run(a),
    };
  });
}

/** The MedicationCard shows its actions in the order given: the destructive one last (left-rail target §3: "Stop … red, last"). */
export function railV3DangerLast(acts: readonly CardAction[]): CardAction[] {
  return [...acts.filter((a) => !a.danger), ...acts.filter((a) => a.danger)];
}
